import Rectangle from "./Rectangle";
import Length from "./Length";
import AbstractShape from "./AbstractShape";
import Area from "./Area";

export default class Droplet extends AbstractShape {
	static NAME = "droplet";
	calculations = {};
	area = null;
	circumference = null;

	constructor( height, width ) {
		super();

		this.height = height;
		this.width = width;
	}

	get_name() {
		return Droplet.NAME;
	}

	get_enclosing_rectangle() {
		const a = this.get_rectangle_side().in_mm();
		const b = ( Math.sqrt( 2 ) / 2 + 1 ) * a;
		return new Rectangle( this.height, new Length( b ) );
	}

	get_rectangle_side() {
		const h = this.height.in_mm();
		const side_length = h / (
			3 / 4 * Math.sqrt( 2 ) + 1 / 2
		);
		return new Length( side_length );
	}

	get_area() {
		if ( ! this.area ) {
			this.calculate_shape();
		}

		return this.area;
	}

	get_circumference() {
		if ( ! this.circumference ) {
			this.calculate_shape();
		}

		return this.circumference;
	}

	calculate_shape() {
		const width = this.width.in_mm();
		const height = this.height.in_mm();

		const base_circle_radius = width / 2;
		const intersect_circle_radius = ( height - base_circle_radius ) / 2;

		const circle_segment_height = this.get_circle_segment_height( base_circle_radius, intersect_circle_radius );

		const chord_length = 2 * Math.sqrt( 2 * base_circle_radius * circle_segment_height - Math.pow( circle_segment_height, 2 ) );
		const triangle_height = height - circle_segment_height;
		const triangle_base = chord_length / 2;
		const triangle_side_length = Math.sqrt( Math.pow( triangle_base, 2 ) + Math.pow( triangle_height, 2 ) );

		const arc_length = base_circle_radius * 2 * Math.acos( 1 - circle_segment_height / base_circle_radius );
		const circumference = 2 * triangle_side_length + arc_length;

		const area = (
			( base_circle_radius * arc_length ) +
			( chord_length * ( circle_segment_height - base_circle_radius ) ) +
			chord_length * Math.sqrt( ( 4 * Math.pow( triangle_side_length, 2 ) - Math.pow( chord_length, 2 ) ) / 4 )
		) / 2;

		this.area = new Area( area );
		this.circumference = new Length( circumference );

		this.calculations = {
			base_circle_radius,
			intersect_circle_radius,
			circle_segment_height,
			chord_length,
			triangle_height,
			triangle_base,
			triangle_side_length,
			arc_length,
			perimeter: circumference,
			area,
		};
	}

	/**
	 * Calculate the circle segment height, which for this shape is the point
	 * where the base circle overlaps with the intersect circle. The Y value of
	 * these intersect points is the circle segment height.
	 */
	get_circle_segment_height( base_circle_radius, intersect_circle_radius ) {
		const distance = intersect_circle_radius;

		// Check if there's an intersection
		if ( distance > ( base_circle_radius + intersect_circle_radius ) || distance < Math.abs( base_circle_radius - intersect_circle_radius ) ) {
			return 0;
		}

		// Calculate the point where these two circles intersect
		const intersect = ( Math.pow( base_circle_radius, 2 ) - Math.pow( intersect_circle_radius, 2 ) + Math.pow( distance, 2 ) ) / ( 2 * distance );

		// Add the base circle radius to get the Y value
		return intersect + base_circle_radius;
	}

	get_height() {
		return this.height;
	}

	get_width() {
		return this.width;
	}

	get_maximum_dimensions( maximum_rectangle ) {
		const shortest_side = maximum_rectangle.get_shortest_side();
		const b             = shortest_side.in_mm();
		const a             = b / (
			Math.sqrt( 2 ) / 2 + 1
		);
		const h             = a * (
			3 / 4 * Math.sqrt( 2 ) + 1 / 2
		);
		return {
			height: new Length( h )
		};
	}

	get_dimension_keys() {
		return [ "height", "width" ];
	}

	get_modifier_keys() {
		return [];
	}

	serialize_primitives() {
		return {
			height: this.height.in_mm(),
			width: this.width.in_mm()
		};
	}

	static deserialize_primitives( primitives ) {
		return new Droplet( new Length( primitives.height ), new Length( primitives.width ) );
	}

}
